import React, { useState } from 'react';
import { bookingApi } from '../../../utils/bookingApi';
import { formatPence, poundsToPence } from '../../../utils/money';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Sheet } from '../../../components/ui';

// Credit some or all of a sent invoice. Each invoice line gets a quantity to
// credit (0 = not credited), at that line's unit price. Extra lines (a
// goodwill discount, say) have their own description and amount.
// With a contact instead of an invoice, it issues credit on account: extra
// lines only, no cap, to be taken off a future invoice.
export default function CreditNoteForm({ invoice, contact, onDone, onCancel }) {
  const onAccount = !invoice && !!contact;
  // What is left to credit on each line, after earlier credit notes
  const alreadyCredited = {};
  for (const cn of invoice?.creditNotes || []) {
    for (const cl of cn.lines || []) {
      if (cl.invoiceLineId) alreadyCredited[cl.invoiceLineId] = (alreadyCredited[cl.invoiceLineId] || 0) + cl.quantity;
    }
  }
  const open = (invoice?.lines || [])
    .map(l => ({ line: l, remaining: l.quantity - (alreadyCredited[l.id] || 0) }))
    .filter(({ remaining }) => remaining > 0);

  const [qty, setQty] = useState(() => Object.fromEntries(open.map(({ line }) => [line.id, '0'])));
  const [extras, setExtras] = useState(() => (onAccount ? [{ description: '', amount: '' }] : []));
  const [reason, setReason] = useState('');
  const emailTo = onAccount ? contact.contactEmail : (invoice.businessContact?.contactEmail || invoice.billToEmail);
  const [email, setEmail] = useState(!!emailTo);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const entered = (id) => (qty[id] === '' ? 0 : Number(qty[id]));
  const chosen = open
    .map(({ line }) => ({ line, n: entered(line.id) }))
    .filter(({ n }) => Number.isInteger(n) && n > 0);
  const setExtra = (i, key) => (e) => setExtras(xs => xs.map((x, j) => (j === i ? { ...x, [key]: e.target.value } : x)));
  // Fully blank extra rows are ignored, like spare rows in the invoice editor
  const filledExtras = extras.filter(x => x.description.trim() || x.amount.trim());
  const total = chosen.reduce((sum, { line, n }) => sum + n * line.unitAmount, 0)
    + filledExtras.reduce((sum, x) => sum + (poundsToPence(x.amount) || 0), 0);

  const creditEverything = () => setQty(Object.fromEntries(open.map(({ line, remaining }) => [line.id, String(remaining)])));

  const submit = async () => {
    setError(null);
    // Never drop a quantity silently: anything not a whole number from 0 to what is left blocks the submit
    const bad = open.find(({ line, remaining }) => {
      const n = entered(line.id);
      return !Number.isInteger(n) || n < 0 || n > remaining;
    });
    if (bad) { setError(`Credit quantity for ${bad.line.description} must be a whole number from 0 to ${bad.remaining}.`); return; }
    const badExtra = filledExtras.find(x => !x.description.trim() || !poundsToPence(x.amount));
    if (badExtra) { setError('Each extra line needs a description and an amount above £0.'); return; }
    if (chosen.length === 0 && filledExtras.length === 0) { setError('Choose at least one line to credit, or add one.'); return; }
    if (!onAccount && total > invoice.amountDue) { setError(`That's more than the ${formatPence(invoice.amountDue)} still due.`); return; }
    setSaving(true);
    try {
      const res = await bookingApi.createCreditNote({
        ...(onAccount ? { businessContactId: contact.id } : { invoiceId: invoice.id }),
        reason, email: emailTo ? email : true,
        lines: [
          ...chosen.map(({ line, n }) => ({ invoiceLineId: line.id, description: line.description, quantity: n, unitAmount: line.unitAmount })),
          ...filledExtras.map(x => ({ description: x.description.trim(), quantity: 1, unitAmount: poundsToPence(x.amount) })),
        ],
      });
      onDone(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't issue the credit note.");
    } finally {
      setSaving(false);
    }
  };

  const dirty = Object.values(qty).some(v => v !== '0') || filledExtras.length > 0 || !!reason.trim();

  return (
    <Sheet
      title={onAccount ? `New credit note for ${contact.name}` : 'New credit note'}
      wide
      onClose={onCancel}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Issuing…' : 'Issue credit note'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="inv-form">
        {!onAccount && open.length === 0 && <p className="inv-muted inv-small" style={{ margin: 0 }}>Every line has already been credited.</p>}
        {!onAccount && <table className="inv-table">
          <thead><tr><th>Line</th><th className="num">Credit qty</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {open.map(({ line: l, remaining }) => (
              <tr key={l.id}>
                <td>{l.description}<br /><span className="inv-muted inv-small">{remaining} left × {formatPence(l.unitAmount)}</span></td>
                <td className="num">
                  <input className="bk-input" style={{ width: '4.5rem' }} type="number" min="0" max={remaining} step="1"
                    aria-label={`Credit quantity for ${l.description}`} value={qty[l.id]}
                    onChange={e => setQty(q => ({ ...q, [l.id]: e.target.value }))} />
                </td>
                <td className="num">{formatPence((Number.isInteger(entered(l.id)) && entered(l.id) > 0 ? entered(l.id) : 0) * l.unitAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>}
        {!onAccount && open.length > 0 && (
          <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={creditEverything}>Credit all</button>
        )}
        {extras.map((x, i) => (
          <div key={i} className="inv-line">
            <label className="inv-field inv-line__desc">
              <span className="inv-muted inv-small">Description</span>
              <input className="bk-input" aria-label={`Extra line ${i + 1} description`} value={x.description} onChange={setExtra(i, 'description')} placeholder="For example, goodwill discount" />
            </label>
            <label className="inv-field">
              <span className="inv-muted inv-small">Amount £</span>
              <input className="bk-input" type="number" min="0.01" step="0.01" inputMode="decimal" aria-label={`Extra line ${i + 1} amount`} value={x.amount} onChange={setExtra(i, 'amount')} />
            </label>
            <span className="inv-actions">
              <button type="button" className="ui-icon-btn" aria-label={`Remove extra line ${i + 1}`} onClick={() => setExtras(xs => xs.filter((_, j) => j !== i))}>
                <XMarkIcon aria-hidden="true" />
              </button>
            </span>
          </div>
        ))}
        <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={() => setExtras(xs => [...xs, { description: '', amount: '' }])}>Add a line</button>
        <div className="inv-totals"><span className="inv-muted inv-small">Credit total</span><strong>{formatPence(total)}</strong></div>
        <span className="inv-muted inv-small">
          {onAccount
            ? `This becomes credit on account for ${contact.name}, to take off a future invoice.`
            : invoice?.applied > 0
              ? 'Crediting everything still due marks the invoice as paid, since credit has already been applied.'
              : 'Crediting everything still due cancels the invoice.'}
        </span>
        <label className="bk-label">Reason <span className="bk-optional">(optional)</span>
          <input className="bk-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="Printed on the credit note" />
        </label>
        {emailTo && (
          <label className="inv-check"><input type="checkbox" checked={email} onChange={e => setEmail(e.target.checked)} /> Email it to {emailTo}</label>
        )}
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}
